import { useEffect, useLayoutEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { X, Sparkles } from "lucide-react";

type Step = {
  selector: string | null; // null = centered welcome/finish
  title: string;
  text: string;
  emoji: string;
};

const STEPS: Step[] = [
  {
    selector: null,
    title: "Willkommen beim Englputzeder Tippspiel! ⚽",
    text: "In 60 Sekunden zeigen wir dir, wie alles funktioniert. Du kannst die Tour jederzeit überspringen.",
    emoji: "👋",
  },
  {
    selector: '[data-tour="next-game"]',
    title: "Nächstes Spiel tippen",
    text: "Hier siehst du das nächste anstehende Spiel. Tippe das Ergebnis — Tipps schließen 15 Min vor Anpfiff.",
    emoji: "🎯",
  },
  {
    selector: '[data-tour="stats"]',
    title: "Deine Punkte & dein Rang",
    text: "Volltreffer +4, Tordifferenz +3, Tendenz +2. Plus bis zu +95 Bonuspunkte. Eure Punkte zählen für die GRUPPENWERTUNG — die 3 besten Tippgruppen gewinnen 5.000 € Sponsoring!",
    emoji: "🏆",
  },
  {
    selector: '[data-tour="tabs"]',
    title: "Spiele filtern",
    text: "Wechsle zwischen kommenden Spielen, Gruppen A–L, KO-Phase und deinen eigenen Tipps.",
    emoji: "📋",
  },
  {
    selector: '[data-tour="nav-tabellen"]',
    title: "Tabellen & KO-Phase",
    text: 'Unter „Tabellen" siehst du die Gruppentabellen und das KO-Bracket — wer trifft wo auf wen.',
    emoji: "📊",
  },
  {
    selector: '[data-tour="nav-gruppen"]',
    title: "Mit Freunden tippen",
    text: "Erstelle eine Gruppe oder tritt mit Code bei. Eure Gruppe sammelt gemeinsam Punkte für die Sponsoring-Wertung.",
    emoji: "👥",
  },
  {
    selector: null,
    title: "🎽 Dein Willkommens-Geschenk",
    text: "Als Dankeschön fürs Mitmachen bekommst du ein GRATIS Englputzeder WM-Trikot + 50 € Gutschein (MBW 1.000 €). Trikots solange der Vorrat reicht — also schnell sein!",
    emoji: "🎁",
  },
  {
    selector: null,
    title: "Los geht's! 🎯",
    text: "Du findest die Spielregeln jederzeit unter Profil → 📖 Spielregeln. Viel Erfolg!",
    emoji: "🚀",
  },
];

type Rect = { top: number; left: number; width: number; height: number };

export function OnboardingTour({ onFinish }: { onFinish: () => void }) {
  const { user, refreshProfile } = useAuth();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = STEPS[i];

  // Measure target on each step change + on resize
  useLayoutEffect(() => {
    if (!step.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) { setRect(null); return; }

    // Initial scroll into view (only on step change)
    el.scrollIntoView({ behavior: "smooth", block: "center" });

    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - 8, left: r.left - 8, width: r.width + 16, height: r.height + 16 });
    };
    // Measure once shortly after scroll settles
    const t = setTimeout(measure, 400);
    // Re-measure on resize / scroll without re-triggering scrollIntoView
    const onUpdate = () => measure();
    window.addEventListener("resize", onUpdate);
    window.addEventListener("scroll", onUpdate, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("scroll", onUpdate, true);
    };
  }, [i, step.selector]);

  const finish = async () => {
    if (user) {
      // Mark the in-app tour as seen. onboardingCompleted was already
      // flipped to true by the signup wizard — this is a separate flag
      // so we can show the tour exactly once after sign-up without
      // disturbing the wizard gate.
      await api.profiles.update({ tourSeen: true });
      await refreshProfile();
    }
    onFinish();
  };

  const next = () => {
    if (i < STEPS.length - 1) setI(i + 1);
    else finish();
  };
  const prev = () => { if (i > 0) setI(i - 1); };

  // Tooltip position — mobile-first: pin to top or bottom of viewport
  // depending on where the highlighted element is, so the card never
  // overflows or covers the cutout on small screens.
  const tooltipStyle: React.CSSProperties = (() => {
    const base: React.CSSProperties = {
      left: 12,
      right: 12,
      maxWidth: 480,
      marginLeft: "auto",
      marginRight: "auto",
    };
    if (!rect) {
      return { ...base, top: "50%", transform: "translateY(-50%)" };
    }
    const vh = window.innerHeight;
    const targetCenter = rect.top + rect.height / 2;
    // If target is in lower half → tooltip at top; otherwise at bottom.
    if (targetCenter > vh / 2) {
      return { ...base, top: 12 };
    }
    return { ...base, bottom: 12 };
  })();

  return (
    <div className="fixed inset-0 z-[100]" aria-modal role="dialog">
      {/* Backdrop with cutout via SVG mask */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={next}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx={12}
                ry={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
        {rect && (
          <rect
            x={rect.left}
            y={rect.top}
            width={rect.width}
            height={rect.height}
            rx={12}
            ry={12}
            fill="none"
            stroke="hsl(var(--accent))"
            strokeWidth={3}
            className="animate-pulse"
            pointerEvents="none"
          />
        )}
      </svg>

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          className="absolute bg-card text-card-foreground rounded-lg shadow-pop p-4 sm:p-5 pointer-events-auto max-h-[80vh] overflow-y-auto"
          style={tooltipStyle}
        >
          <button
            onClick={finish}
            aria-label="Tour schließen"
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground tap p-1"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-2.5 sm:gap-3 pr-6">
            <div className="text-2xl sm:text-3xl shrink-0">{step.emoji}</div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-extrabold text-base sm:text-lg leading-tight">
                {step.title}
              </h3>
              <p className="text-[13px] sm:text-sm text-muted-foreground mt-1.5">{step.text}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5 justify-center mt-3 sm:mt-4">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-pill transition-all ${idx === i ? "w-6 bg-accent" : "w-1.5 bg-muted"}`}
              />
            ))}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2 mt-3 sm:mt-4">
            {i > 0 && (
              <Button variant="outline" onClick={prev} className="sm:flex-1 h-11 font-bold">← Zurück</Button>
            )}
            {i === 0 && (
              <Button variant="outline" onClick={finish} className="sm:flex-1 h-11 font-bold">Überspringen</Button>
            )}
            <Button
              onClick={next}
              className="sm:flex-1 h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
            >
              {i === STEPS.length - 1 ? "Los geht's 🎯" : i === 0 ? <><Sparkles className="h-4 w-4 mr-1" /> Tour starten</> : "Weiter →"}
            </Button>
          </div>

          <div className="text-center text-[11px] text-muted-foreground mt-2">
            Schritt {i + 1} von {STEPS.length}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
